/**
 * Detect when selected services exceed a salon day’s open hours
 * and pack them into Day 1 / Day 2 / … like multi-day deals.
 */

const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

const parseClockToMinutes = (raw) => {
  if (!raw) return null
  const text = String(raw).trim()
  const match = text.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i)
  if (!match) return null
  let hour = parseInt(match[1], 10)
  const minutes = match[2] ? parseInt(match[2], 10) : 0
  const period = (match[3] || '').toLowerCase()
  if (period === 'pm' && hour < 12) hour += 12
  if (period === 'am' && hour === 12) hour = 0
  if (!period && hour > 23) return null
  return hour * 60 + minutes
}

/** "9:00 AM - 8:00 PM" → open span in minutes, or null if closed/invalid */
export const parseOpenSpanMinutes = (hoursStr) => {
  if (hoursStr == null) return null
  const trimmed = String(hoursStr).trim()
  const lower = trimmed.toLowerCase()
  if (!trimmed || lower === 'closed' || lower === 'close' || lower === 'off') {
    return null
  }
  const rangeMatch = trimmed.match(
    /(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)\s*[-–—]\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/i
  )
  if (!rangeMatch) return null
  const start = parseClockToMinutes(rangeMatch[1])
  const end = parseClockToMinutes(rangeMatch[2])
  if (start == null || end == null || end <= start) return null
  return end - start
}

const resolveHoursForWeekday = (businessHours, weekdayIndex) => {
  if (!businessHours || typeof businessHours !== 'object') return null
  const key = DAY_KEYS[weekdayIndex]
  const capitalized = key.charAt(0).toUpperCase() + key.slice(1)
  if (businessHours[key] != null) return businessHours[key]
  if (businessHours[capitalized] != null) return businessHours[capitalized]

  // Range keys like "Monday - Friday"
  for (const [label, value] of Object.entries(businessHours)) {
    const lower = String(label).toLowerCase()
    if (lower.includes(' - ') || lower.includes('–')) {
      const parts = lower.split(/\s*[-–]\s*/)
      const startIdx = DAY_KEYS.findIndex((d) => parts[0]?.includes(d.slice(0, 3)))
      const endIdx = DAY_KEYS.findIndex((d) => parts[1]?.includes(d.slice(0, 3)))
      if (startIdx >= 0 && endIdx >= 0) {
        if (startIdx <= endIdx) {
          if (weekdayIndex >= startIdx && weekdayIndex <= endIdx) return value
        } else if (weekdayIndex >= startIdx || weekdayIndex <= endIdx) {
          return value
        }
      }
    }
    if (lower.includes(key) || lower.includes(key.slice(0, 3))) return value
  }
  return null
}

/** Open minutes for a given Date (local). */
export const getOpenMinutesForDate = (businessHours, date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  return parseOpenSpanMinutes(resolveHoursForWeekday(businessHours, d.getDay()))
}

/** Fallback when salon hours are missing/unparseable (matches typical 9 AM–5 PM). */
export const DEFAULT_OPEN_MINUTES = 8 * 60

/**
 * Open minutes used for overflow checks.
 * - With a selected booking date → that day's hours
 * - Before a date is chosen → longest open weekday (never "today" alone,
 *   so a short today does not force a multi-day split for future bookings)
 */
export const getRepresentativeOpenMinutes = (businessHours, date = null) => {
  let hours = businessHours
  if (typeof hours === 'string') {
    try {
      hours = JSON.parse(hours)
    } catch (_) {
      const span = parseOpenSpanMinutes(hours)
      return span && span > 0 ? span : DEFAULT_OPEN_MINUTES
    }
  }

  if (date != null && date !== '') {
    const d = date instanceof Date ? date : new Date(date)
    if (!Number.isNaN(d.getTime())) {
      const selectedSpan = getOpenMinutesForDate(hours, d)
      if (selectedSpan && selectedSpan > 0) return selectedSpan
      // Selected day closed / unparseable — fall through to longest open day
    }
  }

  let max = 0
  for (let i = 0; i < 7; i += 1) {
    const span = parseOpenSpanMinutes(resolveHoursForWeekday(hours, i))
    if (span && span > max) max = span
  }
  return max > 0 ? max : DEFAULT_OPEN_MINUTES
}

export const parseServiceDurationMinutes = (service) => {
  if (!service) return 60
  if (typeof service.duration_minutes === 'number' && service.duration_minutes > 0) {
    return Math.floor(service.duration_minutes)
  }
  const durationStr = service.duration || service.durationMinutes || '60 min'
  const first = String(durationStr).split('-')[0].trim()
  let minutes = 0
  const hrMatch = first.match(/(\d+(?:[.,]\d+)?)\s*hr/i)
  const minMatch = first.match(/(\d+)\s*min/i)
  if (hrMatch) minutes += Math.round(parseFloat(hrMatch[1].replace(',', '.')) * 60)
  if (minMatch) minutes += parseInt(minMatch[1], 10)
  if (!hrMatch && !minMatch) {
    const num = first.match(/\d+/)
    if (num) minutes += parseInt(num[0], 10)
  }
  return minutes > 0 ? minutes : 60
}

export const sumServicesDurationMinutes = (services = []) =>
  (services || []).reduce((sum, s) => sum + parseServiceDurationMinutes(s), 0)

export const formatDurationHoursLabel = (totalMinutes) => {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0))
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`
  const hours = Math.floor(mins / 60)
  const rem = mins % 60
  if (rem === 0) {
    const whole = mins / 60
    const label = Number.isInteger(whole) ? String(whole) : whole.toFixed(1)
    return `${label} hour${whole === 1 ? '' : 's'}`
  }
  return `${hours}h ${rem}m`
}

/**
 * Greedy pack: keep adding services to the current day while they fit.
 * A single service longer than openMinutes still gets its own day (hard case).
 */
export const packServicesIntoDays = (services = [], openMinutes) => {
  const list = Array.isArray(services) ? services : []
  const open = Math.max(1, Math.floor(Number(openMinutes) || 0))
  if (list.length === 0) return { services: [], totalDays: 1, dayDurations: [] }

  let dayNumber = 1
  let dayUsed = 0
  const dayDurations = []
  const packed = list.map((service) => {
    const dur = parseServiceDurationMinutes(service)
    if (dayUsed > 0 && dayUsed + dur > open) {
      dayDurations[dayNumber - 1] = dayUsed
      dayNumber += 1
      dayUsed = 0
    }
    dayUsed += dur
    return { ...service, day_number: dayNumber }
  })
  dayDurations[dayNumber - 1] = dayUsed

  return {
    services: packed,
    totalDays: dayNumber,
    dayDurations,
  }
}

export const buildOverflowMessage = (totalMinutes, openMinutes, totalDays, options = {}) => {
  const need = formatDurationHoursLabel(totalMinutes)
  const open = formatDurationHoursLabel(openMinutes)
  const days = Math.max(2, Number(totalDays) || 2)
  const openPhrase = options.hasSelectedDate
    ? `but the salon is only open ${open} on the selected day.`
    : `but the salon’s longest open day is only ${open}.`
  return (
    `These services need about ${need}, ${openPhrase}\n` +
    `You can book them across ${days} days.`
  )
}

/**
 * @returns {null | {
 *   needed: true,
 *   totalMinutes: number,
 *   openMinutes: number,
 *   totalDays: number,
 *   services: array,
 *   message: string,
 *   deal: object
 * }}
 */
export const evaluateDurationOverflow = (services, businessHours, options = {}) => {
  const list = Array.isArray(services) ? services : []
  if (list.length === 0) return null

  // Already a multi-day deal — don't re-split
  if (options.existingDeal?.total_days > 1) return null

  const hasSelectedDate = options.date != null && options.date !== ''
  const openMinutes =
    getRepresentativeOpenMinutes(businessHours, hasSelectedDate ? options.date : null) ||
    DEFAULT_OPEN_MINUTES

  const totalMinutes = sumServicesDurationMinutes(list)
  if (totalMinutes <= openMinutes) return null

  const packed = packServicesIntoDays(list, openMinutes)
  const totalDays = Math.max(2, packed.totalDays)

  return {
    needed: true,
    totalMinutes,
    openMinutes,
    totalDays,
    services: packed.services,
    message: buildOverflowMessage(totalMinutes, openMinutes, totalDays, { hasSelectedDate }),
    deal: {
      id: null,
      title: 'Multi-day booking',
      discountPercent: 0,
      serviceIds: packed.services.map((s) => s.id).filter(Boolean),
      total_days: totalDays,
      isDurationSplit: true,
      // Not a hero package / offer — only a schedule split
      isPackageOffer: false,
    },
  }
}

export default evaluateDurationOverflow
